/**
 * Vestra Analytics v1 — the web's typed emit door.
 *
 * Every v1 event the web sends goes through `captureV1`, which takes the
 * event's properties exactly as the shared catalog types them
 * (AnalyticsV1EventProperties in @bash/shared). It is a typed face on the
 * lib/posthog façade, so the queue-until-init and consent gate still apply.
 */

import { posthog } from "@/lib/posthog";
import { isDesktopShell } from "@/lib/desktop-shell";
import { ANALYTICS_EVENTS } from "@bash/shared";
import type {
  AnalyticsClientSurface,
  AnalyticsDelegationSurface,
  AnalyticsInteractionResolution,
  AnalyticsV1EventName,
  AnalyticsV1EventProperties,
  InteractionOutcome,
} from "@bash/shared";

export function captureV1<E extends AnalyticsV1EventName>(
  event: E,
  properties: AnalyticsV1EventProperties[E]
): void {
  // Never throws: emit sites sit in the middle of real work (a send before its
  // emit), and observability must never break the caller.
  try {
    posthog.capture(event, properties);
  } catch {
    // a lost event, never a lost send
  }
}

/** delegation:submitted for an ask the composer gateway lane carries (typed on the web). */
export function captureComposerSubmitted(
  surface: AnalyticsDelegationSurface,
  text: string,
  taskId?: string
): void {
  captureV1(ANALYTICS_EVENTS.DELEGATION_SUBMITTED, {
    ...(taskId ? { task_id: taskId } : {}),
    surface,
    input_mode: "typed",
    message_length: text.length,
  });
}

/** What an interaction outcome says the person did: declined it, answered its questions, or allowed it. */
export function resolutionOf(
  outcome: InteractionOutcome
): AnalyticsInteractionResolution {
  if (outcome.behavior === "deny") return "denied";
  const input = outcome.updatedInput as { answers?: unknown } | undefined;
  return input?.answers !== undefined ? "answered" : "approved";
}

/** The OD2 trace surface this client is: the desktop shell's webview or a browser. */
export function clientSurface(): AnalyticsClientSurface {
  return isDesktopShell() ? "native-app" : "web";
}
